import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import React, { useEffect, useRef } from "react";
import Chart from "react-google-charts";
import { GoogleChartWrapper, GoogleViz } from "react-google-charts/dist/types";
import { pieGraphDefaultColorScheme } from "./GraphColorSchemes";
import { Grid, Typography, useTheme } from "@mui/material";
import { XValueType } from "utils/ReportDataToGoogleFormat";

type GoogleDataFormat = any[][];

interface OwnProps {
    chartId?: string; // used to identify the chart in the onSelect callback

    data?: GoogleDataFormat;
    countData?: GoogleDataFormat; // used for the total count in the center of the donut graph
    legendData?: GoogleDataFormat; // optional paginated rows for legend display
    previousData?: GoogleDataFormat;
    colorScheme?: string[];

    legendPosition: "none" | "bottom" | "labeled" | "left" | "right" | "top";

    onSelect?: (
        series: string | null,
        x: XValueType | null,
        y: string | number | null | any,
        chartId?: string,
        seriesIdentifier?: string | null,
    ) => void;

    innerRadius?: number; // 0 <= innerRadius <= 1
    sliceText?: "percentage" | "value" | "label" | "none";

    chartAreaLeftOffset?: number | string;
    chartAreaTopOffset?: number | string;
    chartAreaHeight?: number | string;
    chartAreaWidth?: number | string;

    showDemoData?: boolean;
    asWidget?: boolean;

    containerHeight?: string | number;

    useAlternateRowLabel?: boolean; // used in AWOverviewChart due to data being out of date on chart type switch

    onChartReady?: (
        chartWrapper: GoogleChartWrapper,
        google: GoogleViz,
    ) => void;

    showLegend?: boolean;
    showTotalCount?: boolean; // if true, shows the total count in the center of the donut graph
    showAccuracy?: boolean; // if true, shows accuracy in the center of the donut graph
}

const AcxDonutGraph: React.FC<OwnProps> = ({ ...props }) => {
    /**
     * The total sits in the donut's hole, so it is sized against the container
     * the donut is drawn in -- in CSS, with container query units.
     *
     * Measuring this in JavaScript does not work here: the component renders
     * nothing until it has data, so any ref or observer set up on the first
     * pass has no element to attach to and never gets a second chance. `cqmin`
     * asks the browser the same question and always has the current answer, so
     * the type tracks the donut through every resize with nothing to keep in
     * sync.
     *
     * The multipliers are the original pixel sizes expressed against the
     * container they were chosen at, and the bounds stop the text turning
     * illegible on a small card or oversized on a large one.
     */
    const totalLabelFontSize = "clamp(9px, 5.6cqmin, 15px)";
    const totalValueFontSize = "clamp(13px, 9.6cqmin, 26px)";
    const scaled = (px: number) =>
        px >= 24 ? totalValueFontSize : totalLabelFontSize;

    const wrapper = useRef<null | any>();
    useEffect(() => {
        return () => {
            wrapper.current?.getChart()?.clearChart();
        };
    }, []);
    const theme = useTheme();

    const data = Boolean(props.showDemoData) ? fake_data : props.data;

    if (!data || data.length === 0) {
        console.warn("AcxDonutGraph data is empty");
        return null;
    }

    const dataNoHeader = [...data];
    dataNoHeader.splice(0, 1); // Remove header row
    const legendSourceData = props.legendData ?? data;
    // Google Chart datasets include a header row at index 0; slice it out for legend item rendering.
    const legendRows = legendSourceData.slice(1);

    const getLabelKey = (value: any): string => {
        if (value && typeof value === "object") {
            const rawValue = (value as { v?: any }).v;
            return rawValue === undefined || rawValue === null
                ? ""
                : `${rawValue}`;
        }

        return value === undefined || value === null ? "" : `${value}`;
    };

    const getLabelDisplay = (value: any): string => {
        if (value && typeof value === "object") {
            const formattedValue = (value as { f?: any }).f;
            return typeof formattedValue === "string" ? formattedValue : "";
        }

        return value === undefined || value === null ? "" : `${value}`;
    };

    const fullDataColorIndexByLabel = new Map<string, number>();
    dataNoHeader.forEach((row, index) => {
        fullDataColorIndexByLabel.set(getLabelKey(row[0]), index);
    });

    const getTotalCount = (data: GoogleDataFormat | undefined) => {
        if (!data || data.length < 2) return 0;
        return data
            .slice(1)
            .reduce(
                (sum, row) => sum + (typeof row[1] === "number" ? row[1] : 0),
                0,
            );
    };

    // Accuracy for Model Performance Charts (True Positive + True Negative) / Total
    const getAccuracy = (data: GoogleDataFormat | undefined) => {
        if (!data || data.length < 3) return 0;
        const truePositives = data[1][1] || 0;
        const trueNegatives = data[2][1] || 0;
        const total = getTotalCount(data);
        return total > 0
            ? Number(
                  (((truePositives + trueNegatives) / total) * 100).toFixed(2),
              )
            : 0;
    };

    const totalCount = getTotalCount(props.countData ?? data);
    const accuracy = getAccuracy(props.countData ?? data);

    // Leaving for reference of the custom tooltip we may want in the future
    // const getTooltipHtml = (label: string, value: number, color: string) => {
    //     return `
    //         <div style="max-width:200px;border-radius:4px;width:fit-content;background:${theme.palette.background.paper};display:flex;align-items:flex-start;flex-direction:column;padding:8px;box-shadow: 0px 0.5px 1.75px 0px rgba(0, 0, 0, 0.04), 0px 1.85px 6.25px 0px rgba(0, 0, 0, 0.19);">
    //             <div style="display:flex;flex-direction:row;align-items:center;">
    //                 <div style="width:12px;height:12px;border-radius:100%;background:${color};margin-right:8px;"></div>
    //                 <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:14px;color:${theme.palette.gray[600]};align-items:stretch">${label}</span>
    //             </div>
    //             <span style="margin-left:20px;font-size:14px;font-weight:600;color:${theme.palette.gray[700]}">${value}</span>
    //         </div>
    //     `;
    // };


    const showLegend = legendRows.length > 1 && props.legendPosition !== "none";
    // If total count (which uses countData) is different from the sum of values in data, we assume data is percentage
    const isPercentage = totalCount !== getTotalCount(data);

    return (
        <Grid
            sx={{
                position: "relative",
                height: "100%",
                width: "100%",
                padding: 3,
            }}
        >
            <Grid
                sx={{
                    display: "grid",
                    gridTemplateColumns: showLegend ? "1fr 1fr" : "1fr",
                    height: "100%",
                    gap: "80px",
                }}
            >
                <Grid
                    sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        justifyContent: showLegend ? "flex-end" : "center",
                        // The text in the hole is sized in `cqmin` against this
                        // box, which is the donut's own box rather than the
                        // card's. When a legend takes half the width, the type
                        // in the hole has to shrink with the ring it sits in.
                        containerType: "size",
                    }}
                >
                    {Boolean(data) && (
                        <Chart
                            /**
                             * Both of these have to be 100%, and that is what
                             * puts the total in the middle of the ring.
                             *
                             * react-google-charts applies this width and height
                             * to two nested elements: the wrapper it puts around
                             * the `render` prop, and the chart container the
                             * `renderChart()` call returns inside it. At 95%
                             * they compound -- the chart container ends up 95%
                             * of the box the total is centred on, anchored to
                             * its top left, so the ring centre sits 2.5% of the
                             * box up and to the left of the text. On a 440x220
                             * box that is the 11px and 6px the total was
                             * visibly off by.
                             *
                             * At 100% the two elements coincide, the ring is
                             * centred in them, and so is the text. The ring's
                             * breathing room comes from the padding on the
                             * container instead, where it is one number rather
                             * than a factor applied twice.
                             */
                            width={props.containerHeight ?? `100%`}
                            height={props.containerHeight ?? `100%`}
                            chartType="PieChart"
                            loader={
                                <AcxLoadingIndicator
                                    color="secondary"
                                    alternate="PuffLoader"
                                    size={96}
                                />
                            }
                            data={
                                Boolean(props.previousData) ? undefined : data
                            }
                            diffdata={
                                Boolean(props.previousData)
                                    ? {
                                          old: props.previousData,
                                          new: data,
                                      }
                                    : undefined
                            }
                            options={{
                                pieSliceText: "none",
                                pieHole: props.innerRadius ?? 0.65,
                                fontName: "Inter",
                                dataOpacity: 0.65,
                                animation: {
                                    startup: true,
                                    duration: 500,
                                    easing: "out",
                                },
                                tooltip: {
                                    isHtml: true,
                                    textStyle: { fontName: "Inter" },
                                    showColorCode: true,
                                },
                                colors:
                                    props.colorScheme &&
                                    props.colorScheme.length
                                        ? props.colorScheme
                                        : pieGraphDefaultColorScheme,
                                pieSliceTextStyle: {
                                    fontName: "Inter",
                                },
                                legend: {
                                    position: "none",
                                },
                                chart: {},
                                chartArea: {
                                    // Pinned rather than left to the library's
                                    // default: the hole has text centred on
                                    // this box, so the box has to be the one
                                    // the ring is actually centred in.
                                    left: props.chartAreaLeftOffset ?? "0%",
                                    // Without an explicit top the pie does not
                                    // sit at the vertical centre of its box, so
                                    // the total in the hole drifts off centre.
                                    top: props.chartAreaTopOffset ?? "5%",
                                    width: props.chartAreaWidth ?? "100%",
                                    height: props.chartAreaHeight ?? "90%",
                                },
                            }}
                            getChartWrapper={(w, g) => {
                                wrapper.current = w;
                                props.onChartReady?.(w, g);
                            }}
                            chartEvents={[
                                {
                                    eventName: "select",
                                    callback: ({ chartWrapper }) => {
                                        const chart = chartWrapper.getChart();
                                        const selection = chart.getSelection();
                                        if (selection.length === 1) {
                                            const [selectedItem] = selection;
                                            const dataTable =
                                                chartWrapper.getDataTable();
                                            const { row, column } =
                                                selectedItem;

                                            if (
                                                row !== null &&
                                                column !== null
                                            ) {
                                                const rowLabel =
                                                    data?.[row + 1]?.[0];

                                                props.onSelect?.(
                                                    dataTable?.getColumnLabel(
                                                        column,
                                                    ) ?? null,
                                                    rowLabel,
                                                    dataTable?.getValue(
                                                        row,
                                                        column,
                                                    ) ?? null,
                                                    props.chartId,
                                                    dataTable?.getColumnId(
                                                        column,
                                                    ) ?? null,
                                                );
                                            } else if (row !== null) {
                                                const rowLabel =
                                                    data?.[row + 1]?.[0];

                                                const alternateRowLabel = (
                                                    dataTable as any
                                                )?.Wf[row]["c"][0]["v"];

                                                // yValues are weird for pieCharts
                                                const yValue =
                                                    dataTable?.getValue(row, 1);

                                                props.onSelect?.(
                                                    null,
                                                    props.useAlternateRowLabel
                                                        ? alternateRowLabel
                                                        : rowLabel,
                                                    yValue,
                                                    props.chartId,
                                                    null,
                                                );
                                            } else if (column !== null) {
                                                props.onSelect?.(
                                                    dataTable?.getColumnLabel(
                                                        column,
                                                    ) ?? null,
                                                    null,
                                                    null,
                                                    props.chartId,
                                                    dataTable?.getColumnId(
                                                        column,
                                                    ) ?? null,
                                                );
                                            }
                                        }
                                    },
                                },
                            ]}
                            render={({ renderChart }) => (
                                <div
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        position: "relative",
                                    }}
                                >
                                    {renderChart()}
                                    {props.showTotalCount && (
                                        <div
                                            id="count container"
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform:
                                                    "translate(-50%, -50%)",
                                                // Stay inside the hole.
                                                maxWidth: "58cqmin",
                                                pointerEvents: "none",
                                                textAlign: "center",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {props.showAccuracy && (
                                                <>
                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                scaled(14),
                                                            color: theme.palette
                                                                .gray[500],
                                                        }}
                                                    >
                                                        Accuracy
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                scaled(16),
                                                            fontWeight:
                                                                theme.typography
                                                                    .fontWeightSemibold,
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        {accuracy.toLocaleString() +
                                                            "%"}
                                                    </Typography>
                                                </>
                                            )}
                                            <Typography
                                                sx={{
                                                    fontSize: scaled(14),
                                                    color: theme.palette
                                                        .gray[500],
                                                }}
                                            >
                                                Total
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: scaled(24),
                                                    fontWeight:
                                                        theme.typography
                                                            .fontWeightSemibold,
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {totalCount.toLocaleString()}
                                            </Typography>
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    )}
                </Grid>
                {showLegend && data && (
                    <Grid
                        id="legend container"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "start",
                            justifyContent: "center",
                            gap: 2,
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {legendRows.map((row, index) => {
                            const globalColorIndex =
                                fullDataColorIndexByLabel.get(
                                    getLabelKey(row[0]),
                                ) ?? index;
                            return (
                                <Grid
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                    }}
                                >
                                    <Grid
                                        sx={{
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "100%",
                                            backgroundColor:
                                                props.colorScheme?.[
                                                    globalColorIndex
                                                ] ??
                                                pieGraphDefaultColorScheme[
                                                    globalColorIndex %
                                                        pieGraphDefaultColorScheme.length
                                                ],
                                            marginRight: 1,
                                            marginY: 0.5,
                                        }}
                                    />
                                    <Typography sx={{ fontSize: "14px" }}>
                                        {getLabelDisplay(row[0])} -{" "}
                                        <span
                                            style={{
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightSemibold,
                                            }}
                                        >
                                            {parseFloat(
                                                row[1].toFixed(
                                                    isPercentage ? 1 : 0,
                                                ),
                                            ).toLocaleString()}
                                            {isPercentage ? "%" : ""}
                                        </span>
                                    </Typography>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

const fake_data = [
    ["Tags", "Count"],
    ["Brand Value", 358293],
    ["Program Value", 101265],
    ["Help With Bill", 172780],
    ["Brand Detractor", 129634],
    ["Program Detractor", 97216],
];

export default AcxDonutGraph;
